// 3rd party dependencies
import {
  Header,
  Table,
  ISelectOption,
  Toggle,
  Notification,
  Tooltip,
  Badge,
  Label,
} from '@salecycle/ui-component-library';
import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Column } from 'react-table';

// Local dependencies
import {
  ISftpFilters,
  ISftpUserStatusUpdateData,
  ISftpUserTableData,
  UserType,
} from 'sections/user-management/entities';
import { NameCell } from '../NameCell';
import { formatClients } from '../../utils/helpers';
import { formatDateTimestamp } from 'utils';
import { SftpFilters } from './SftpFilters';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import { Site, Group, Brand } from 'sections/client-management/entities';
import { useClients } from 'core/app/AppContext';
import { entityToSortedSelectOptions } from 'components/utils/getSelectOptions';
import { TooltipHtml } from 'sections/design/template-management/sections/template-editor/components/common/TooltipHtml';
import ConfirmationModal from 'components/experimental/ConfirmationModal';

interface ITableValue {
  original: ISftpUserTableData;
  value: any;
}

interface ITableFilter {
  id: string;
  value: string | boolean;
}

interface TypeValues {
  value: UserType;
  color: string;
}

const userTypeMap: { [key in UserType]: TypeValues } = {
  INTERNAL: {
    value: 'INTERNAL',
    color: '#CC6200',
  },
  EXTERNAL: {
    value: 'EXTERNAL',
    color: '#1F7A3F',
  },
};

const getColumns = (
  handleToggleUserClick: (username: string, email: string, checked: boolean) => void,
  t: TFunction,
  filters: ISftpFilters,
  getClientSelectOptions: (type: string) => ISelectOption<string>[],
): Column[] => {
  const Name = (props: ITableValue): JSX.Element => (
    <NameCell
      data-testid="NameCell"
      name={props.value}
      id={props.original.username}
      url="/user-management/sftp-users/edit-sftp-user"
    />
  );

  const StatusToggle = (props: ITableValue): JSX.Element => {
    const isChecked = props.original.enabled;

    return (
      <Toggle
        data-testid="sftp-user-enabled"
        checked={isChecked}
        margin={false}
        onChange={(checked: boolean): void =>
          handleToggleUserClick(props.original.username, props.original.email, checked)
        }
        ariaLabel={t('sftpUserManagement:sftpUsers.table.toggleUser.label', {
          context: isChecked ? 'enabled' : 'disabled',
          email: props.original.email,
        })}
      />
    );
  };

  const TypeBadge = (props: ITableValue): JSX.Element => {
    if (!props.value) {
      return null;
    }

    const { color, value } = userTypeMap[props.value.toUpperCase()];
    const name = t(`sftpUserManagement:sftpUsers.userType.${value.toLowerCase()}`).toUpperCase();

    return (
      <Badge color="#F5F7FA" size="small">
        <Label isBold color={color} name={name} />
      </Badge>
    );
  };

  return [
    {
      Header: t('sftpUserManagement:sftpUsers.table.columns.username'),
      id: 'username',
      accessor: 'username',
      Cell: Name,
      minWidth: 150,
    },
    {
      Header: (
        <Tooltip
          html={
            <TooltipHtml
              heading={t('sftpUserManagement:sftpUsers.table.columns.email')}
              description={t('sftpUserManagement:sftpUsers.tooltips.email')}
            />
          }
          position="top"
          wrapperStyle={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <>{t('sftpUserManagement:sftpUsers.table.columns.email')}</>
        </Tooltip>
      ),
      accessor: 'email',
    },
    {
      Header: t('sftpUserManagement:sftpUsers.table.columns.userType'),
      id: 'userType',
      accessor: (d: ISftpUserTableData): string => d.userType,
      Cell: TypeBadge,
    },
    {
      Header: t('sftpUserManagement:sftpUsers.table.columns.createdDate'),
      id: 'createdDate',
      accessor: (d: ISftpUserTableData): number => d.createdDate,
      Cell: (props: ITableValue): string => formatDateTimestamp(props.value),
    },
    {
      Header: t('sftpUserManagement:sftpUsers.table.columns.groups'),
      id: 'group',
      accessor: (d: ISftpUserTableData): string => formatClients(d.groups, getClientSelectOptions('group')),
    },
    {
      Header: t('sftpUserManagement:sftpUsers.table.columns.brands'),
      id: 'brand',
      accessor: (d: ISftpUserTableData): string => formatClients(d.brands, getClientSelectOptions('brand')),
    },
    {
      Header: t('sftpUserManagement:sftpUsers.table.columns.sites'),
      id: 'site',
      accessor: (d: ISftpUserTableData): string => formatClients(d.sites, getClientSelectOptions('site')),
    },
    {
      Header: t('sftpUserManagement:sftpUsers.table.columns.enabled'),
      id: 'enabled',
      accessor: (d: ISftpUserTableData): boolean => !filters.enabledSftpUsers || d.enabled,
      Cell: StatusToggle,
    },
  ];
};

const customFilter = (filter: ITableFilter, row: ISftpUserTableData, filters: ISftpFilters): boolean => {
  const { id, value } = filter;

  if (row[id] !== undefined) {
    const rowValue = typeof value === 'string' ? row[id].toLowerCase() : row[id];

    const filterClients = (label: string): boolean => {
      if (typeof value === 'string') {
        if (value === '') {
          return true;
        }
        const clients = rowValue.split(', ');
        return clients.includes(label);
      }
    };

    switch (id) {
      case 'enabled':
        return !filter.value || row.enabled;
      case 'email':
      case 'username':
        return typeof value === 'string' && rowValue.includes(value.toLowerCase());
      case 'group':
        return filterClients(filters.group?.label?.toLowerCase());
      case 'brand':
        return filterClients(filters.brand?.label?.toLowerCase());
      case 'site':
        return filterClients(filters.site?.label?.toLowerCase());
    }
  } else if (value !== '') {
    return false;
  }

  return true;
};

export interface IProps {
  tableData: ISftpUserTableData[];
  handleNewSftpUserClick: () => void;
}

export const SftpUserTable = ({ tableData, handleNewSftpUserClick }: IProps): JSX.Element => {
  const { t } = useTranslation(['sftpUserManagement', 'common']);
  const { getSites, getBrands, getGroups } = useClients();

  const [showModal, setShowModal] = useState(false);
  const [modalOptions, setModalOptions] = useState({ heading: '', description: '', onOkButtonClicked: null });
  const [pageNumber, setPageNumber] = useState(0);
  const [filters, setFilters] = useState({
    username: '',
    email: '',
    group: null,
    brand: null,
    site: null,
    userType: null,
    enabledSftpUsers: true,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [sftpUserNumber, setSftpUserNumber] = useState<number>(0);

  const groupSelectOptions = entityToSortedSelectOptions<Group>(getGroups());
  const brandSelectOptions = entityToSortedSelectOptions<Brand>(getBrands());
  const siteSelectOptions = entityToSortedSelectOptions<Site>(getSites());

  const getClientSelectOptions = (type: string): ISelectOption<string>[] => {
    switch (type) {
      case 'site':
        return siteSelectOptions;
      case 'brand':
        return brandSelectOptions;
      case 'group':
        return groupSelectOptions;
    }
  };

  const putSftpUserEnabled = useMutation((sftpUser: ISftpUserStatusUpdateData) => {
    return RestApi.put(`/public_sftp_users/${sftpUser.username}/status`, sftpUser);
  });

  useEffect((): void => {
    let tableDataCopy = [...tableData];
    if (filters.enabledSftpUsers) {
      tableDataCopy = tableDataCopy.filter(it => it.enabled === true);
    }
    if (filters.username) {
      tableDataCopy = tableDataCopy.filter(it => it.username.toLowerCase().includes(filters.username.toLowerCase()));
    }
    if (filters.email) {
      tableDataCopy = tableDataCopy.filter(it => it.email.toLowerCase().includes(filters.email.toLowerCase()));
    }
    if (filters.group) {
      tableDataCopy = tableDataCopy.filter(it => it.groups.toLowerCase() === filters.group.value.toLowerCase());
    }
    if (filters.brand) {
      tableDataCopy = tableDataCopy.filter(it => it.brands.toLowerCase() === filters.brand.value.toLowerCase());
    }
    if (filters.site) {
      tableDataCopy = tableDataCopy.filter(it => it.sites.toLowerCase() === filters.site.value.toLowerCase());
    }
    setSftpUserNumber(tableDataCopy.length);
  }, [filters, tableData]);

  const updateFilters = (value: string | boolean | ISelectOption<string>, type: string): void => {
    setFilters({ ...filters, [type]: value });
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const toggleUser = async (username: string, enabled: boolean): Promise<void> => {
    putSftpUserEnabled.mutate(
      { username, enabled },
      {
        onSuccess: () => {
          // Only update the user if the backend call is successful
          const user = tableData.find(u => u.username === username);
          user.enabled = enabled;
        },
        onError: (error: RestApiError, variables, context) => {
          setErrorMessage(error.message);
        },
      },
    );
  };

  const handleToggleUserClick = (username: string, email: string, enabled: boolean) => {
    setModalOptions({
      description: t('common:userManagement.users.table.toggleUser.modal.description', {
        context: enabled ? 'enabled' : filters.enabledSftpUsers ? 'disabledHidden' : 'disabled',
        email,
      }),
      heading: t('common:userManagement.users.table.toggleUser.modal.heading', {
        context: enabled ? 'enabled' : 'disabled',
      }),
      onOkButtonClicked: () => {
        closeModal();
        toggleUser(username, enabled);
      },
    });

    setShowModal(true);
  };

  const handleFilterChange = (value: string | boolean | ISelectOption<string>, type: string): void => {
    updateFilters(value, type);
  };

  const defaultFilterMethod = (filter: ITableFilter, row: ISftpUserTableData): boolean =>
    customFilter(filter, row, filters);

  const handleNotificationClose = (isOpen: boolean): void => {
    if (!isOpen) {
      setErrorMessage('');
    }
  };
  return (
    <>
      <Notification isOpen={!!errorMessage} variation="error" onChange={handleNotificationClose}>
        {errorMessage}
      </Notification>

      <ConfirmationModal
        cancelButtonLabel={t('common:userManagement.users.table.toggleUser.modal.cancel')}
        description={modalOptions.description}
        heading={modalOptions.heading}
        isOpen={showModal}
        okButtonLabel={t('common:userManagement.users.table.toggleUser.modal.ok')}
        onCancelButtonClicked={closeModal}
        onCloseButtonClicked={closeModal}
        onOkButtonClicked={modalOptions.onOkButtonClicked}
      />

      <SftpFilters
        handleFilterChange={handleFilterChange}
        getClientSelectOptions={getClientSelectOptions}
        filters={filters}
        handleNewSftpUserClick={handleNewSftpUserClick}
      />
      <Header heading={`${t('sftpUserManagement:sftpUsers.heading')}(${sftpUserNumber})`} />

      <Table
        columns={getColumns(handleToggleUserClick, t, filters, getClientSelectOptions)}
        data={tableData}
        filtered={[
          { id: 'username', value: filters.username },
          { id: 'email', value: filters.email },
          { id: 'group', value: filters.group ? filters.group.value : '' },
          { id: 'brand', value: filters.brand ? filters.brand.value : '' },
          { id: 'site', value: filters.site ? filters.site.value : '' },
          { id: 'enabled', value: filters.enabledSftpUsers },
        ]}
        defaultFilterMethod={defaultFilterMethod}
        defaultPageSize={10}
        defaultSorted={[{ id: 'username', desc: false }]}
        page={pageNumber}
        onPageChange={(pageIndex: number): void => {
          setPageNumber(pageIndex);
        }}
        onPageSizeChange={(pageSize: number, pageIndex: number): void => {
          setPageNumber(pageIndex);
        }}
        onSortedChange={(): void => {
          setPageNumber(0);
        }}
        noDataText={t('sftpUserManagement:sftpUsers.table.noData')}
      />
    </>
  );
};

export default SftpUserTable;
